/* eslint-disable jsx-a11y/accessible-emoji */
import React, { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';

import { StandardButton } from '@eika/button';

import {
    getAppUrl,
    getExternalUrlForSkadekategori,
    getExternalUrlForSkadetype,
} from '../landingsside/deep-link-handler';
import { Skadekategori } from '../landingsside/deep-link-handler/skadekategori';
import { logger } from '../logg/logger';
import { urler } from '../router/router-urler';
import { Skademeldingstype } from '../types/generated-types';
import { skadeUrlFromSkadetype } from '../urler';

const {
    ettersendingUrl,
    klageUrl,
    husOgHytteUrl,
    innboOgEiendelerUrl,
    degOgDineUforhetDodsfallEllerKritiskSykdomUrl,
    reiseUrl,
    kjoretoyUrl,
    batUrl,
    naringUrl,
    naringNaringsbyggOgEiendelerUrl,
    landbrukUrl,
    landbrukProduksjonsdyrUrl,
    dyrUrl,
    rettshjelpUrl,
    ansvarUrl,
    partner,
} = urler();

const LandingssideLenke: FunctionComponent<{ tittel: string; url: string }> = ({ tittel, url }) => {
    return (
        <div className="developer-page-element">
            <a href={`#${url}`}>{tittel}</a>
        </div>
    );
};

const EksternLandingssideLenke: FunctionComponent<{ tittel: string; url: string }> = ({ tittel, url }) => {
    return (
        <div className="developer-page-element">
            <a className="link-title link-wrapper" href={url}>
                <h2>{tittel} </h2>
            </a>
        </div>
    );
};

const SitecoreLandingssideLenke: FunctionComponent<{
    tittel: string;
    url: string;
    sitecoreUrl: string;
}> = ({ tittel, url, sitecoreUrl }) => {
    return (
        <div className="developer-page-element">
            <div>
                <Link to={url}>{tittel}</Link>
            </div>
            <a href={sitecoreUrl}> ➡ Sitecore </a>
            <div />
        </div>
    );
};

export enum Environment {
    TEST = 'test',
    QA = 'qa',
    LOCALHOST = 'localhost',
}

export const isEnvironment = (environment: Environment, currentUrl: string = getAppUrl()): boolean => {
    return environment.split(' ').some((value) => currentUrl.includes(value));
};

export const isNonProductionEnvironment = (currentUrl: string = getAppUrl()) =>
    isEnvironment(Environment.LOCALHOST, currentUrl) ||
    isEnvironment(Environment.TEST, currentUrl) ||
    isEnvironment(Environment.QA, currentUrl);

export const DeveloperPage = () => {
    const loggInnNettbankUrl = () => 'https://68nord.test.eika.no/felles-fakelogin-web/';
    const folgSkadeUrl = () => 'https://68nord.test.eika.no/nettbank/forsikring/folg-skade';

    return (
        <>
            <div className="developer-page-group">
                <h1 className="developer-page-title">Kategorier</h1>
                <div className="link-group">
                    <SitecoreLandingssideLenke
                        tittel="Kjøretoy 🚗"
                        url={kjoretoyUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Kjoretoy)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Elsparkesykkel 🛴"
                        url={skadeUrlFromSkadetype(Skademeldingstype.SmaElektriskeKjoretoy)}
                        sitecoreUrl={getExternalUrlForSkadetype(Skademeldingstype.SmaElektriskeKjoretoy)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Båt ⛴"
                        url={batUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Bat)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Reise 🚢"
                        url={reiseUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Reise)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Bygning og eiendom, hus og hytte 🏡)"
                        url={husOgHytteUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.HusOgHytte)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Innbo og eiendeler 🛋"
                        url={innboOgEiendelerUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Innbo)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Deg og dine 👩‍👩‍👧‍👧 - Uførhet, kritisk sykdom eller dødsfall 👨‍🦽💀"
                        url={degOgDineUforhetDodsfallEllerKritiskSykdomUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(
                            Skadekategori.DegOgDineUforhetDodsfallEllerKritiskSykdom,
                        )}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Deg og dine - Ulykke  🍌"
                        url={skadeUrlFromSkadetype(Skademeldingstype.PersonskadeUlykke)}
                        sitecoreUrl={getExternalUrlForSkadetype(Skademeldingstype.PersonskadeUlykke)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Deg og dine - Barne- og ungdomsforsikring  👶🍼"
                        url={skadeUrlFromSkadetype(Skademeldingstype.PersonskadeBarneOgUngdomsforsikring)}
                        sitecoreUrl={getExternalUrlForSkadetype(Skademeldingstype.PersonskadeBarneOgUngdomsforsikring)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Dyr 🐕"
                        url={dyrUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Dyr)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Næring 🏢"
                        url={naringUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Naring)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Næringsbygg og eiendeler 🏢"
                        url={naringNaringsbyggOgEiendelerUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.NaringNaringsbyggOgEiendeler)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Landbruk 🚜"
                        url={landbrukUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Landbruk)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Produksjonsdyr 🐄"
                        url={landbrukProduksjonsdyrUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.LandbrukProduksjonsdyr)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Produksjonsdyr 🐄"
                        url={landbrukProduksjonsdyrUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.LandbrukProduksjonsdyr)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Ansvar 👩🏻‍⚖️"
                        url={ansvarUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Ansvar)}
                    />

                    <SitecoreLandingssideLenke
                        tittel="Rettshjelp 👮‍"
                        url={rettshjelpUrl}
                        sitecoreUrl={getExternalUrlForSkadekategori(Skadekategori.Rettshjelp)}
                    />

                    <LandingssideLenke tittel="Ettersending (Husk innlogging) 💌" url={ettersendingUrl} />
                    <LandingssideLenke tittel="Klage 🤬" url={klageUrl} />
                </div>
            </div>

            <div className="developer-page-group">
                <h1 className="developer-page-title">Annet</h1>
                <div className="link-group">
                    <LandingssideLenke tittel="Partner 🧑‍🤝‍🧑" url={partner} />
                    {isEnvironment(Environment.TEST) && (
                        <EksternLandingssideLenke tittel="Innlogging nettbank 🔓" url={loggInnNettbankUrl()} />
                    )}
                    {isEnvironment(Environment.TEST) && (
                        <EksternLandingssideLenke tittel="Følg skade 🩸🚶" url={folgSkadeUrl()} />
                    )}
                    <StandardButton
                        onClick={() => {
                            const log = logger('7270');
                            log.warn(
                                `Developer Page - Generated Test Error.`,
                                'En personlig id',
                                Skademeldingstype.Feilfylling,
                                new Error('Det skjedde en feil!'),
                            );
                        }}
                    >
                        Generer feil i frontend (skademelding feilet under innsending) 🤡
                    </StandardButton>
                </div>
            </div>
        </>
    );
};
